import { Link, useNavigate, useLocation } from 'react-router-dom';
import { VoteIcon, HomeIcon, LogOutIcon, MenuIcon, User, Compass, CoinsIcon, ShieldIcon } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-hot-toast';
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import type { Profile } from '@/types/database.types';
import { getSafeImageUrl } from '@/utils/image-utils';

export default function Navbar() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [imageError, setImageError] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // First try to get existing profile
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (existingProfile) {
          // console.log("Navbar: Found existing profile with avatar_url:", existingProfile.avatar_url);
          setProfile(existingProfile);
          
          // Check if user is admin (for demo purposes, we'll use a specific email)
          setIsAdmin(user.email === 'dennishorn@gmx.net');
          return;
        }

        // If no profile exists, create one with default values
        const username = `user_${user.id.substring(0, 8)}`;
        const { data: newProfile, error: createError } = await supabase
          .from('profiles')
          .insert({
            user_id: user.id,
            username,
            avatar_url: null
          })
          .select()
          .single();

        if (createError) throw createError;
        if (newProfile) {
          // console.log("Navbar: Created new profile");
          setProfile(newProfile);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    fetchProfile();
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      toast.error('Failed to sign out');
    }
  };

  const handleImageError = () => {
    // console.log("Navbar: Image error loading avatar");
    setImageError(true);
  };

  const defaultAvatar = 'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png?20150327203541';
  const avatarUrl = !imageError && profile?.avatar_url ? getSafeImageUrl(profile.avatar_url) : defaultAvatar;

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="bg-gradient-to-r from-blue-200 to-blue-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <Link to="/dashboard" className="flex items-center px-2 py-2 text-blue-900">
              <VoteIcon className="h-8 w-8" />
              <span className="ml-2 text-xl font-bold">SnapVote.ai</span>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center sm:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-blue-900 hover:text-blue-700 hover:bg-blue-100"
            >
              <MenuIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Desktop navigation */}
          <div className="hidden sm:flex sm:items-center sm:space-x-4">
            <Link
              to="/dashboard"
              className={`inline-flex items-center px-3 py-2 text-sm font-medium ${
                isActive('/dashboard') 
                  ? 'text-blue-900 bg-blue-100 rounded-md' 
                  : 'text-blue-900 hover:text-blue-700'
              }`}
            >
              <HomeIcon className="h-5 w-5 mr-1" />
              Dashboard
            </Link>
            
            <Link
              to="/feed"
              className={`inline-flex items-center px-3 py-2 text-sm font-medium ${
                isActive('/feed') 
                  ? 'text-blue-900 bg-blue-100 rounded-md' 
                  : 'text-blue-900 hover:text-blue-700'
              }`}
            >
              <Compass className="h-5 w-5 mr-1" />
              SNAP Feed
            </Link>
            
            <Link
              to="/credits"
              className={`inline-flex items-center px-3 py-2 text-sm font-medium ${
                isActive('/credits') 
                  ? 'text-blue-900 bg-blue-100 rounded-md' 
                  : 'text-blue-900 hover:text-blue-700'
              }`}
            >
              <CoinsIcon className="h-5 w-5 mr-1" />
              Credits
            </Link>
            
            <Link
              to="/create"
              className="hidden lg:inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <VoteIcon className="h-5 w-5 mr-1" />
              Create SNAP
            </Link>
            
            {isAdmin && (
              <Link
                to="/admin/cashsnaps"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-900 hover:text-blue-700"
              >
                <ShieldIcon className="h-5 w-5 mr-1" />
                Admin
              </Link>
            )}
            
            <Link
              to="/profile"
              className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-blue-900 hover:text-blue-700"
            >
              <img
                src={avatarUrl}
                alt={profile?.username || 'Profile'}
                onError={handleImageError}
                className="h-8 w-8 rounded-full object-cover ring-2 ring-white"
              />
              <span>{profile?.username || 'Profile'}</span>
            </Link>
            <button
              onClick={handleSignOut}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-900 hover:text-red-600"
            >
              <LogOutIcon className="h-5 w-5 mr-1" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="sm:hidden bg-white border-t border-blue-200">
          <div className="pt-2 pb-3 space-y-1">
            <Link
              to="/dashboard"
              className={`block px-3 py-2 text-base font-medium ${
                isActive('/dashboard') 
                  ? 'text-blue-900 bg-blue-50' 
                  : 'text-blue-900 hover:text-blue-700 hover:bg-blue-50'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              <div className="flex items-center">
                <HomeIcon className="h-5 w-5 mr-2" />
                Dashboard
              </div>
            </Link>
            
            <Link
              to="/feed"
              className={`block px-3 py-2 text-base font-medium ${
                isActive('/feed') 
                  ? 'text-blue-900 bg-blue-50' 
                  : 'text-blue-900 hover:text-blue-700 hover:bg-blue-50'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              <div className="flex items-center">
                <Compass className="h-5 w-5 mr-2" />
                SNAP Feed
              </div>
            </Link>
            
            <Link
              to="/credits"
              className={`block px-3 py-2 text-base font-medium ${
                isActive('/credits') 
                  ? 'text-blue-900 bg-blue-50' 
                  : 'text-blue-900 hover:text-blue-700 hover:bg-blue-50'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              <div className="flex items-center">
                <CoinsIcon className="h-5 w-5 mr-2" />
                Credits
              </div>
            </Link>
            
            <Link
              to="/create"
              className="block px-3 py-2 text-base font-medium text-blue-900 hover:text-blue-700 hover:bg-blue-50"
              onClick={() => setIsMenuOpen(false)}
            >
              <div className="flex items-center">
                <VoteIcon className="h-5 w-5 mr-2" />
                Create SNAP
              </div>
            </Link>
            
            {isAdmin && (
              <Link
                to="/admin/cashsnaps"
                className="block px-3 py-2 text-base font-medium text-blue-900 hover:text-blue-700 hover:bg-blue-50"
                onClick={() => setIsMenuOpen(false)}
              >
                <div className="flex items-center">
                  <ShieldIcon className="h-5 w-5 mr-2" />
                  Admin
                </div>
              </Link>
            )}
            
            <Link
              to="/profile"
              className="block px-3 py-2 text-base font-medium text-blue-900 hover:text-blue-700 hover:bg-blue-50"
              onClick={() => setIsMenuOpen(false)}
            >
              <div className="flex items-center space-x-2">
                <img
                  src={avatarUrl}
                  alt={profile?.username || 'Profile'}
                  onError={handleImageError}
                  className="h-8 w-8 rounded-full object-cover ring-2 ring-white"
                />
                <span>{profile?.username || 'Profile'}</span>
              </div>
            </Link>
            <button
              onClick={() => {
                handleSignOut();
                setIsMenuOpen(false);
              }}
              className="block w-full text-left px-3 py-2 text-base font-medium text-blue-900 hover:text-red-600 hover:bg-blue-50"
            >
              <div className="flex items-center">
                <LogOutIcon className="h-5 w-5 mr-2" />
                Sign Out
              </div>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}